import type { ApiClient } from '@sentinel/api-client';

const STORAGE_KEY = 'sentinel_access_expires_at';
const LOCK_NAME = 'sentinel:refresh';
const LOST_RACE_TIMEOUT_MS = 12_000;
// Fallback access-token lifetime (seconds) used only when a successful rotation
// response omits `expires_in`. The cookie-mode `/auth/token` refresh response
// (`{ "message": "token refreshed" }`) carries no `expires_in` — the new TTL
// lives only in the HttpOnly cookie the SPA can't read. We schedule a
// conservative early re-rotation rather than block the session: refreshing
// sooner than necessary is harmless; treating a real rotation as a failure is not.
const FALLBACK_TTL_SECONDS = 300;

export type RotateStatus = 'ok' | 'lost-race' | 'unauthorized' | 'transient-failure';

/**
 * Discriminated union: `expiresAt` is only present on the `'ok'` branch, so
 * callers narrow via `status` rather than using a non-null assertion.
 */
export type RotateResult =
  | { status: 'ok'; expiresAt: number }
  | { status: 'lost-race' }
  | { status: 'unauthorized' }
  | { status: 'transient-failure' };

export function readExpiresAt(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function writeExpiresAt(t: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(t));
  } catch {
    // Safari Private mode / quota exceeded — silently fall back to in-tab-only behaviour.
  }
}

export function clearExpiresAt(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same fallback rationale as writeExpiresAt.
  }
}

/**
 * Wait for a peer tab to publish a new `expires_at`. Resolves on the first
 * matching storage event, or after `LOST_RACE_TIMEOUT_MS` to guard against
 * a holder tab that crashes mid-rotation (between acquiring the lock and
 * writing the new value). On timeout we still resolve (not reject) — the
 * scheduler's next tick re-reads localStorage and will rotate again if the
 * value is still stale.
 */
function waitForExpiresAtUpdate(): Promise<void> {
  return new Promise<void>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    function cleanup() {
      window.removeEventListener('storage', onStorage);
      if (timer !== null) clearTimeout(timer);
    }
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) {
        cleanup();
        resolve();
      }
    }
    window.addEventListener('storage', onStorage);
    timer = setTimeout(() => {
      cleanup();
      resolve();
    }, LOST_RACE_TIMEOUT_MS);
  });
}

/**
 * Rotate the access token via `/auth/token` with `grant_type=refresh_token`.
 * Backend reads the refresh_token from the HttpOnly cookie. On success,
 * writes the new `expires_at` to localStorage so peer tabs see it.
 *
 * Cross-tab safety: serialised via `navigator.locks`. A second tab that
 * tries to rotate while a holder is in flight returns `'lost-race'`
 * (after the holder writes the new `expires_at`), so callers can re-read
 * the freshened value without firing a second `/auth/token` call.
 */
export async function rotateAccessToken(client: ApiClient): Promise<RotateResult> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) {
    // Very old browser (none in our matrix today). Fall back to a plain call;
    // cross-tab race becomes possible but is recoverable.
    return performRotate(client);
  }
  return locks.request(LOCK_NAME, { ifAvailable: true }, async (lock) => {
    if (lock === null) {
      await waitForExpiresAtUpdate();
      return { status: 'lost-race' };
    }
    return performRotate(client);
  });
}

async function performRotate(client: ApiClient): Promise<RotateResult> {
  const res = await client.POST('/auth/token', {
    // `body` MUST be defined: openapi-fetch short-circuits and never calls
    // `bodySerializer` when `body === undefined`, which shipped an empty-body
    // POST and a 400 invalid_request from the backend. The serializer ignores
    // this value and emits the fixed form payload.
    body: { grant_type: 'refresh_token' },
    bodySerializer: () => 'grant_type=refresh_token',
    // Intentionally NO `SKIP_UNAUTH_HEADER`: when /auth/token returns 401
    // (refresh token revoked/expired/family-reused), we want the api-client's
    // unauth middleware to fire `sentinel:unauthenticated` so portal's
    // UnauthRedirectListener navigates the user to /login immediately rather
    // than sitting in a dead-session limbo until their next click triggers a
    // protected-API 401. The dispatcher's `fired` latch and the scheduler's
    // own `stopped = true` latch keep this single-shot.
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
  });
  const status = res.response?.status;
  if (status === 401) {
    clearExpiresAt();
    return { status: 'unauthorized' };
  }
  // Any non-2xx (or a missing response = network/CORS failure) is a transient
  // failure: the scheduler retries later, and a cold-load probe falls through
  // to the login form.
  if (status === undefined || status < 200 || status >= 300) {
    return { status: 'transient-failure' };
  }
  // 2xx: rotation succeeded and the backend has set fresh HttpOnly cookies.
  // Honour `expires_in` when present (bearer/JSON mode); otherwise fall back to
  // a conservative TTL. Critically, a 2xx WITHOUT `expires_in` (the cookie-mode
  // `{ "message": "token refreshed" }` response) is still success — treating it
  // as a failure here is what stranded a freshly-rotated user on /login.
  const data = res.data;
  const ttlSeconds =
    data && typeof data.expires_in === 'number' ? data.expires_in : FALLBACK_TTL_SECONDS;
  const expiresAt = Date.now() + ttlSeconds * 1000;
  writeExpiresAt(expiresAt);
  return { status: 'ok', expiresAt };
}
